/**
 * Données du questionnaire Big Five (personnalité)
 * 20 questions de tri forcé (ipsatif) réparties sur 4 pages de 5 questions.
 *
 * Principe : pour chaque question, le candidat doit classer 3 phrases
 * déclaratives de la plus représentative de lui (rang 1) à la moins
 * représentative (rang 3), via un drag-and-drop.
 *
 * Les 5 dimensions du Big Five évaluées :
 * - O : Ouverture à l'expérience (Openness)
 * - C : Conscienciosité (Conscientiousness)
 * - E : Extraversion
 * - A : Agréabilité (Agreeableness)
 * - N : Neuroticisme (Neuroticism) / Stabilité émotionnelle
 */

export const PAMI_QUESTIONS_PER_PAGE = 1;

export interface PamiStatement {
  /** Identifiant unique de la phrase (utilisé comme valeur de tri) */
  id: string;

  prefixLabel: string; // "A. ", "B. ", "C. " (pour affichage devant la phrase)
  /** Texte de la phrase déclarative présentée au candidat */
  label: string;
}

interface PamiQuestion {
  /** Identifiant unique de la question (utilisé comme clé de formulaire) */
  name: string;
  /** Les 3 phrases à classer par ordre de préférence */
  statements: [PamiStatement, PamiStatement, PamiStatement];
}

/**
 * Consigne affichée en tête du questionnaire / de chaque question.
 * Toutes les questions partagent la même consigne : pas de label individuel.
 */
export const pamiInstructions =
  "Classez les 3 propositions de celle qui vous correspond le plus (première position) à celle qui vous correspond le moins (dernière position). Répondez de manière spontanée et sincère, il n'y a pas de bonnes ou de mauvaises réponses.";

/**
 * Version courte, affichée sur chaque question : la consigne complète est
 * identique pour les 41 questions, la répéter en entier devient du bruit.
 * Elle reste accessible d'un clic (et dépliée par défaut sur la 1re question).
 */
export const pamiInstructionsShort =
  "Classez ces 3 propositions selon ce qui vous ressemble le plus.";

export const pamiQuestions: PamiQuestion[] = [
  {
    name: "q1",
    statements: [
      {
        id: "Statut4",
        prefixLabel: "A. ",
        label:
          "Je veux participer à des événements importants (conférences, salons).",
      },
      {
        id: "O1i6",
        prefixLabel: "B. ",
        label: "J'aime analyser des problèmes sous plusieurs angles.",
      },
      {
        id: "O2i6",
        prefixLabel: "C. ",
        label: "Je trouve facilement des idées originales.",
      },
    ],
  },
  {
    name: "q2",
    statements: [
      {
        id: "Auto1",
        prefixLabel: "A. ",
        label:
          "Je veux utiliser mes propres méthodes pour atteindre les objectifs.",
      },
      {
        id: "Secu1",
        prefixLabel: "B. ",
        label: "Je veux un contrat de travail pérenne.",
      },
      {
        id: "H2i5",
        prefixLabel: "C. ",
        label:
          "Je préfère perdre une opportunité plutôt que de compromettre mes principes.",
      },
    ],
  },
  {
    name: "q3",
    statements: [
      {
        id: "O1i2",
        prefixLabel: "A. ",
        label: "J'apprécie les activités qui stimulent l'esprit.",
      },
      {
        id: "C2i4",
        prefixLabel: "B. ",
        label: "Je livre un travail de qualité.",
      },
      {
        id: "Manag4",
        prefixLabel: "C. ",
        label:
          "Je veux bénéficier d'un leadership bienveillant.",
      },
    ],
  },
  {
    name: "q4",
    statements: [
      {
        id: "H1i4",
        prefixLabel: "A. ",
        label:
          "Je préfère être honnête plutôt que manipuler pour obtenir ce que je veux.",
      },
      {
        id: "Statut5",
        prefixLabel: "B. ",
        label:
          "Je veux gagner en visibilité grâce à mon titre professionnel.",
      },
      {
        id: "C2i3",
        prefixLabel: "C. ",
        label:
          "Je réalise consciencieusement le travail qui m'est confié.",
      },
    ],
  },
  {
    name: "q5",
    statements: [
      {
        id: "Equi2",
        prefixLabel: "A. ",
        label: "Je veux pouvoir télétravailler.",
      },
      {
        id: "E2i8",
        prefixLabel: "B. ",
        label: "J'apprécie être entouré(e) de monde.",
      },
      {
        id: "Auto6",
        prefixLabel: "C. ",
        label: "Je veux être libre dans la réalisation de mon travail.",
      },
    ],
  },
  {
    name: "q6",
    statements: [
      {
        id: "H1i7",
        prefixLabel: "A. ",
        label:
          "Je ne dis pas ce que l'on attend de moi juste pour faire bonne impression.",
      },
      {
        id: "H2i7",
        prefixLabel: "B. ",
        label: "J'agis de façon honnête en toutes circonstances.",
      },
      {
        id: "C1i2",
        prefixLabel: "C. ",
        label: "Je termine un projet que j'ai commencé.",
      },
    ],
  },
  {
    name: "q7",
    statements: [
      {
        id: "Rev4",
        prefixLabel: "A. ",
        label:
            "Je veux être rémunéré(e) pour des missions exceptionnelles ou des heures supplémentaires.",
      },
      {
        id: "O1i5",
        prefixLabel: "B. ",
        label: "J'aime lire des ouvrages qui me font réfléchir.",
      },
      {
        id: "Manag2",
        prefixLabel: "C. ",
        label:
          "Je veux des retours réguliers et constructifs sur mon travail.",
      },
    ],
  },
  {
    name: "q8",
    statements: [
      {
        id: "E2i2",
        prefixLabel: "A. ",
        label:
          "Je suis à l'aise dans des événements qui rassemblent beaucoup de monde.",
      },
      {
        id: "O2i7",
        prefixLabel: "B. ",
        label: "J'ai besoin d'exprimer ma créativité.",
      },
      {
        id: "N1i1",
        prefixLabel: "C. ",
        label: "Je suis détendu(e) la plupart du temps.",
      },
    ],
  },
  {
    name: "q9",
    statements: [
      {
        id: "E2i3",
        prefixLabel: "A. ",
        label: "Je préfère avoir des gens autour de moi plutôt qu'être seul(e).",
      },
      {
        id: "N2i5",
        prefixLabel: "B. ",
        label:
          "Je ne me laisse pas submerger par mes émotions dans les situations difficiles.",
      },
      {
        id: "H2i1",
        prefixLabel: "C. ",
        label:
          "Je refuse de tirer profit d'une situation même si personne ne le découvrirait.",
      },
    ],
  },
  {
    name: "q10",
    statements: [
      {
        id: "Var1",
        prefixLabel: "A. ",
        label:
          "Je veux travailler sur des projets différents.",
      },
      {
        id: "H1i1",
        prefixLabel: "B. ",
        label:
          "Je suis authentique et ne trompe pas les autres.",
      },
      {
        id: "H2i4",
        prefixLabel: "C. ",
        label:
          "Je respecte les règles, même quand je pourrais les contourner sans risque.",
      },
    ],
  },
  {
    name: "q11",
    statements: [
      {
        id: "E1i2",
        prefixLabel: "A. ",
        label: "Je dirige les choses quand je fais partie d'un groupe.",
      },
      {
        id: "A2i3",
        prefixLabel: "B. ",
        label: "Je suis toujours disponible pour apporter mon aide.",
      },
      {
        id: "E2i7",
        prefixLabel: "C. ",
        label:
          "J'engage la conversation avec des personnes que je ne connais pas.",
      },
    ],
  },
  {
    name: "q12",
    statements: [
      {
        id: "Equi4",
        prefixLabel: "A. ",
        label:
          "Je veux avoir du temps pour mes loisirs en dehors du travail.",
      },
      {
        id: "N1i4",
        prefixLabel: "B. ",
        label: "Je n'ai pas tendance à me faire du souci.",
      },
      {
        id: "C2i2",
        prefixLabel: "C. ",
        label: "Je suis attentivement les consignes qu'on me donne.",
      },
    ],
  },
  {
    name: "q13",
    statements: [
      {
        id: "Secu5",
        prefixLabel: "A. ",
        label: "Je veux un poste pérenne dans mon entreprise/organisation.",
      },
      {
        id: "N2i3",
        prefixLabel: "B. ",
        label: "Je garde mon sang-froid dans les situations difficiles.",
      },
      {
        id: "A1i5",
        prefixLabel: "C. ",
        label: "Je privilégie le dialogue aux rapports de force.",
      },
    ],
  },
  {
    name: "q14",
    statements: [
      {
        id: "E1i3",
        prefixLabel: "A. ",
        label: "J'aime animer les réunions et mener les discussions.",
      },
      {
        id: "O2i2",
        prefixLabel: "B. ",
        label: "J'aime laisser libre cours à mon imagination.",
      },
      {
        id: "H1i5",
        prefixLabel: "C. ",
        label: "J'évite de mentir, même quand cela me servirait.",
      },
    ],
  },
  {
    name: "q15",
    statements: [
      {
        id: "Rea1",
        prefixLabel: "A. ",
        label: "Je veux voir les effets de mon travail.",
      },
      {
        id: "E1i1",
        prefixLabel: "B. ",
        label: "Je m'affirme sans hésitation.",
      },
      {
        id: "O2i4",
        prefixLabel: "C. ",
        label: "Je prends plaisir à imaginer des choses nouvelles.",
      },
    ],
  },
  {
    name: "q16",
    statements: [
      {
        id: "Equi1",
        prefixLabel: "A. ",
        label:
          "Je veux pouvoir ajuster mes horaires en fonction de mes obligations personnelles.",
      },
      {
        id: "Manag8",
        prefixLabel: "B. ",
        label:
          "Je veux être reconnu(e) et respecté(e) dans mon travail.",
      },
      {
        id: "E2i5",
        prefixLabel: "C. ",
        label: "Je vais facilement vers les autres.",
      },
    ],
  },
  {
    name: "q17",
    statements: [
      {
        id: "Rev5",
        prefixLabel: "A. ",
        label:
          "Je veux des primes liées à mes performances individuelles.",
      },
      {
        id: "Rea8",
        prefixLabel: "B. ",
        label: "Je veux me sentir accompli(e) dans mon travail.",
      },
      {
        id: "Secu6",
        prefixLabel: "C. ",
        label:
          "Je veux travailler dans une entreprise financièrement stable.",
      },
    ],
  },
  {
    name: "q18",
    statements: [
      {
        id: "Rev6",
        prefixLabel: "A. ",
        label: "Je veux des augmentations de salaire.",
      },
      {
        id: "A2i5",
        prefixLabel: "B. ",
        label: "Je suis sensible aux besoins des autres.",
      },
      {
        id: "C1i4",
        prefixLabel: "C. ",
        label: "Je respecte les échéances que je me fixe.",
      },
    ],
  },
  {
    name: "q19",
    statements: [
      {
        id: "O1i7",
        prefixLabel: "A. ",
        label:
          "J'apprécie les discussions profondes et argumentées.",
      },
      {
        id: "N2i7",
        prefixLabel: "B. ",
        label: "Je parviens à prendre du recul quand je suis stressé(e).",
      },
      {
        id: "C2i5",
        prefixLabel: "C. ",
        label: "Je respecte les délais qui me sont fixés.",
      },
    ],
  },
  {
    name: "q20",
    statements: [
      {
        id: "Auto7",
        prefixLabel: "A. ",
        label:
          "Je veux organiser mon travail comme je l'entends.",
      },
      {
        id: "Equi8",
        prefixLabel: "B. ",
        label:
          "Je ne veux pas sacrifier ma vie personnelle au profit du travail.",
      },
      {
        id: "Rea2",
        prefixLabel: "C. ",
        label:
          "Je veux que la qualité de mon travail soit reconnue.",
      },
    ],
  },
  {
    name: "q21",
    statements: [
      {
        id: "N1i7",
        prefixLabel: "A. ",
        label: "Je suis serein(e) la plupart du temps.",
      },
      {
        id: "Auto4",
        prefixLabel: "B. ",
        label:
          "Je veux prendre des initiatives sans demander d'autorisation.",
      },
      {
        id: "O1i8",
        prefixLabel: "C. ",
        label: "Je suis stimulé(e) par les défis intellectuels.",
      },
    ],
  },
  {
    name: "q22",
    statements: [
      {
        id: "Auto5",
        prefixLabel: "A. ",
        label: "Je veux prendre mes propres décisions.",
      },
      {
        id: "O1i3",
        prefixLabel: "B. ",
        label: "J'ai une grande curiosité intellectuelle.",
      },
      {
        id: "E2i4",
        prefixLabel: "C. ",
        label: "Je suis rapidement à l'aise dans un groupe.",
      },
    ],
  },
  {
    name: "q23",
    statements: [
      {
        id: "Rev1",
        prefixLabel: "A. ",
        label:
          "Je veux un salaire aligné sur mes compétences et mon expérience.",
      },
      {
        id: "H1i2",
        prefixLabel: "B. ",
        label: "Je reste sincère, même si cela me coûte un avantage.",
      },
      {
        id: "C2i7",
        prefixLabel: "C. ",
        label: "Je tiens mes engagements professionnels.",
      },
    ],
  },
  {
    name: "q24",
    statements: [
      {
        id: "Var2",
        prefixLabel: "A. ",
        label: "Je veux alterner entre plusieurs types de tâches au travail.",
      },
      {
        id: "Manag3",
        prefixLabel: "B. ",
        label:
          "Je veux pouvoir discuter ouvertement de mes difficultés avec mon supérieur.",
      },
      {
        id: "N2i6",
        prefixLabel: "C. ",
        label: "Je garde une attitude posée, même sous stress.",
      },
    ],
  },
  {
    name: "q25",
    statements: [
      {
        id: "E1i8",
        prefixLabel: "A. ",
        label: "Je sais faire entendre ma voix.",
      },
      {
        id: "A2i4",
        prefixLabel: "B. ",
        label:
          "Je prends volontiers du temps pour aider quelqu'un en difficulté.",
      },
      {
        id: "Manag1",
        prefixLabel: "C. ",
        label:
          "Je veux un manager qui valorise mon développement professionnel.",
      },
    ],
  },
  {
    name: "q26",
    statements: [
      {
        id: "Rea4",
        prefixLabel: "A. ",
        label: "Je veux valoriser mes compétences au travail.",
      },
      {
        id: "A1i4",
        prefixLabel: "B. ",
        label: "Je fais de mon mieux pour désamorcer les conflits.",
      },
      {
        id: "N2i2",
        prefixLabel: "C. ",
        label:
          "Je prends les bonnes décisions en situation de stress.",
      },
    ],
  },
  {
    name: "q27",
    statements: [
      {
        id: "H1i3",
        prefixLabel: "A. ",
        label:
          "Je ne cherche pas à séduire quelqu'un dans mon intérêt personnel.",
      },
      {
        id: "Auto8",
        prefixLabel: "B. ",
        label:
          "Je veux travailler sans être constamment encadré(e).",
      },
      {
        id: "Rea5",
        prefixLabel: "C. ",
        label:
          "Je veux atteindre des objectifs professionnels ambitieux.",
      },
    ],
  },
  {
    name: "q28",
    statements: [
      {
        id: "Secu7",
        prefixLabel: "A. ",
        label:
          "Je préfère éviter les situations professionnelles trop risquées ou instables.",
      },
      {
        id: "Var3",
        prefixLabel: "B. ",
        label: "Je ne veux pas toujours faire la même chose au travail.",
      },
      {
        id: "C1i6",
        prefixLabel: "C. ",
        label: "Je me fixe des priorités claires et je m'y tiens.",
      },
    ],
  },
  {
    name: "q29",
    statements: [
      {
        id: "Statut6",
        prefixLabel: "A. ",
        label:
          "Je veux accéder à un réseau professionnel important.",
      },
      {
        id: "Manag7",
        prefixLabel: "B. ",
        label:
          "Je veux travailler en me sentant soutenu(e) par ma hiérarchie.",
      },
      {
        id: "A1i3",
        prefixLabel: "C. ",
        label:
          "Je préfère coopérer avec les autres plutôt que d'être en conflit avec eux.",
      },
    ],
  },
  {
    name: "q30",
    statements: [
      {
        id: "Var7",
        prefixLabel: "A. ",
        label:
          "Je veux des journées de travail qui ne se ressemblent pas.",
      },
      {
        id: "H2i8",
        prefixLabel: "B. ",
        label: "Je fais ce qui est juste.",
      },
      {
        id: "Rea3",
        prefixLabel: "C. ",
        label:
          "Je veux ressentir une fierté professionnelle à la fin d'une mission ou d'un projet.",
      },
    ],
  },
  {
    name: "q31",
    statements: [
      {
        id: "Statut7",
        prefixLabel: "A. ",
        label:
          "Je veux un poste qui suscite une certaine reconnaissance sociale.",
      },
      {
        id: "O2i8",
        prefixLabel: "B. ",
        label: "J'aime créer des choses.",
      },
      {
        id: "H2i6",
        prefixLabel: "C. ",
        label: "Je me comporte de manière intègre.",
      },
    ],
  },
  {
    name: "q32",
    statements: [
      {
        id: "Secu8",
        prefixLabel: "A. ",
        label:
          "Je veux un emploi stable.",
      },
      {
        id: "Equi3",
        prefixLabel: "B. ",
        label: "Je veux bénéficier de jours de congés flexibles.",
      },
      {
        id: "N1i2",
        prefixLabel: "C. ",
        label: "Je suis rarement nerveux(se).",
      },
    ],
  },
  {
    name: "q33",
    statements: [
      {
        id: "Rev2",
        prefixLabel: "A. ",
        label:
          "Je veux pouvoir financer mon style de vie idéal grâce à mon travail.",
      },
      {
        id: "E1i4",
        prefixLabel: "B. ",
        label: "Je prends volontiers la parole dans un groupe.",
      },
      {
        id: "Equi5",
        prefixLabel: "C. ",
        label:
          "Je veux déconnecter en dehors des heures de bureau.",
      },
    ],
  },
  {
    name: "q34",
    statements: [
      {
        id: "E1i7",
        prefixLabel: "A. ",
        label: "Je défends mon point de vue avec assurance.",
      },
      {
        id: "O2i1",
        prefixLabel: "B. ",
        label: "J'aime explorer des idées innovantes.",
      },
      {
        id: "N2i4",
        prefixLabel: "C. ",
        label:
          "Je gère le stress sans me laisser déstabiliser.",
      },
    ],
  },
  {
    name: "q35",
    statements: [
      {
        id: "Rev7",
        prefixLabel: "A. ",
        label: "Je veux des perspectives d'évolution salariale.",
      },
      {
        id: "Var5",
        prefixLabel: "B. ",
        label: "Je veux des missions diverses et variées.",
      },
      {
        id: "C2i1",
        prefixLabel: "C. ",
        label:
          "Je fais mon travail même quand je n'en ai pas envie.",
      },
    ],
  },
  {
    name: "q36",
    statements: [
      {
        id: "Secu4",
        prefixLabel: "A. ",
        label: "Je veux un revenu régulier.",
      },
      {
        id: "C1i8",
        prefixLabel: "B. ",
        label: "Je poursuis mes objectifs sans me laisser distraire.",
      },
      {
        id: "N1i5",
        prefixLabel: "C. ",
        label: "Je n'ai pas tendance à m'inquiéter pour de petites choses.",
      },
    ],
  },
  {
    name: "q37",
    statements: [
      {
        id: "A2i7",
        prefixLabel: "A. ",
        label: "Je me rends disponible pour aider les autres.",
      },
      {
        id: "Var6",
        prefixLabel: "B. ",
        label:
          "Je veux de la variété dans mes activités professionnelles.",
      },
      {
        id: "A1i8",
        prefixLabel: "C. ",
        label: "Je fais preuve de diplomatie dans les situations tendues.",
      },
    ],
  },
  {
    name: "q38",
    statements: [
      {
        id: "A2i6",
        prefixLabel: "A. ",
        label:
          "Je fais passer les besoins des autres avant les miens quand c'est nécessaire.",
      },
      {
        id: "N1i3",
        prefixLabel: "B. ",
        label: "Je suis rarement inquiet(e) ou anxieux(se).",
      },
      {
        id: "A1i1",
        prefixLabel: "C. ",
        label: "J'évite d'être en conflit avec les autres.",
      },
    ],
  },
  {
    name: "q39",
    statements: [
      {
        id: "Statut1",
        prefixLabel: "A. ",
        label: "Je veux travailler pour une entreprise prestigieuse.",
      },
      {
        id: "C1i3",
        prefixLabel: "B. ",
        label: "Je suis constant(e) dans mon travail, même sans supervision.",
      },
      {
        id: "A1i6",
        prefixLabel: "C. ",
        label:
          "Je cherche à comprendre le point de vue de l'autre en cas de désaccord.",
      },
    ],
  },
  {
    name: "q40",
    statements: [
      {
        id: "Statut3",
        prefixLabel: "A. ",
        label: "Je veux pouvoir mentionner mon employeur avec fierté.",
      },
      {
        id: "A2i8",
        prefixLabel: "B. ",
        label: "Je prête main-forte sans qu'on me le demande.",
      },
      {
        id: "C1i5",
        prefixLabel: "C. ",
        label: "Je vais au bout de ce que j'entreprends.",
      },
    ],
  },
];
